import { createContext, Fragment, useContext, useEffect, useLayoutEffect, useRef, useState, type DragEvent as ReactDragEvent, type MouseEvent, type ReactNode } from 'react';
import {
  Activity,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpRight,
  BarChart3,
  BriefcaseBusiness,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  CircleDot,
  CircleHelp,
  Clock3,
  Gauge,
  Inbox,
  Mail,
  MessageCircle,
  Network,
  PhoneCall,
  PhoneMissed,
  Server,
  ShieldCheck,
  Ticket as TicketIcon,
  Timer,
  Users,
  Workflow,
  Zap,
  GripVertical,
} from 'lucide-react';
import { Button, Card } from '../../components/ui';
import type { MonitoringSnapshot, MonitoringStatus } from '../monitoring/MonitoringPage';
import type {
  DashboardData,
  DashboardDiagnostics,
  DashboardPlannedWork,
  DashboardStatusMetrics,
  DashboardStatusPeriod,
  DashboardTicket,
  DashboardTrendData,
  DashboardTrendPeriod,
  ServerMetricsSnapshot,
} from './dashboard-types';
import type { DashboardWidgetKey } from './dashboard-widgets';
import { NetworkPlot, TicketTrendPlot } from './DashboardCharts';
import { networkWindow, type NetworkSample } from './dashboard-chart-geometry';

const TREND_OPTIONS: Array<{ value: DashboardTrendPeriod; label: string }> = [
  { value: '7d', label: '7 дней' },
  { value: '30d', label: '30 дней' },
  { value: '90d', label: '90 дней' },
  { value: 'month', label: 'Текущий месяц' },
  { value: 'year', label: 'Текущий год' },
];

type DiagnosticsState = 'loading' | 'ready' | 'error';
type Navigate = (path: string) => void;
type DashboardRole = 'admin' | 'manager' | 'specialist' | 'client';

const DASHBOARD_MASONRY_ROW_PX = 8;

export function dashboardMasonrySpan(height: number, rowGap: number, rowHeight = DASHBOARD_MASONRY_ROW_PX) {
  const safeHeight = Math.max(0, Number(height) || 0);
  const safeGap = Math.max(0, Number(rowGap) || 0);
  const safeRow = Math.max(1, Number(rowHeight) || DASHBOARD_MASONRY_ROW_PX);
  return Math.max(1, Math.ceil((safeHeight + safeGap) / (safeRow + safeGap)));
}

function dashboardCards(grid: HTMLElement) {
  return Array.from(grid.children).filter((node): node is HTMLElement => (
    node instanceof HTMLElement && node.classList.contains('dashboard-role-card')
  ));
}

function shouldPreviewDashboardTarget(
  sourceIndex: number,
  targetIndex: number,
  point: { x: number; y: number },
  sourceRect: DOMRect,
  targetRect: DOMRect,
) {
  if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) return false;
  if (targetRect.width <= 0 && targetRect.height <= 0) return true;
  const overlapY = Math.max(0, Math.min(sourceRect.bottom, targetRect.bottom) - Math.max(sourceRect.top, targetRect.top));
  const sameRow = overlapY >= Math.min(sourceRect.height, targetRect.height) * .45;
  const axisSize = sameRow ? targetRect.width : targetRect.height;
  const midpoint = sameRow
    ? targetRect.left + targetRect.width / 2
    : targetRect.top + targetRect.height / 2;
  const pointer = sameRow ? point.x : point.y;
  const hysteresis = Math.min(24, Math.max(8, axisSize * .08));
  return sourceIndex < targetIndex
    ? pointer >= midpoint + hysteresis
    : pointer <= midpoint - hysteresis;
}

export type DashboardRoleViewsProps = {
  data: DashboardData;
  trend?: DashboardTrendData | null;
  trendPeriod: DashboardTrendPeriod;
  trendLoading: boolean;
  statusMetrics?: DashboardStatusMetrics | null;
  statusPeriod: DashboardStatusPeriod;
  statusLoading: boolean;
  enabledWidgets: DashboardWidgetKey[];
  layoutSaving: boolean;
  onPreviewWidgetMove: (key: DashboardWidgetKey, targetKey: DashboardWidgetKey) => void;
  onCommitWidgetMove: () => void;
  onCancelWidgetMove: () => void;
  diagnostics?: DashboardDiagnostics | null;
  diagnosticsState: DiagnosticsState;
  monitoring?: MonitoringSnapshot | null;
  monitoringState: DiagnosticsState;
  serverMetrics?: ServerMetricsSnapshot | null;
  serverMetricsState: DiagnosticsState;
  onNavigate: Navigate;
  onRetryDiagnostics: () => void;
  onRetryMonitoring: () => void;
  onRetryServerMetrics: () => void;
  onTrendPeriodChange: (period: DashboardTrendPeriod) => void;
  onStatusPeriodChange: (period: DashboardStatusPeriod) => void;
};

type LayoutEditorContextValue = {
  saving: boolean;
  orderedKeys: DashboardWidgetKey[];
  draggedKey: DashboardWidgetKey | null;
  dropKey: DashboardWidgetKey | null;
  start: (key: DashboardWidgetKey, dataTransfer: DataTransfer) => void;
  preview: (targetKey: DashboardWidgetKey) => void;
  drop: () => void;
  end: () => void;
};

const LayoutEditorContext = createContext<LayoutEditorContextValue>({
  saving: false,
  orderedKeys: [],
  draggedKey: null,
  dropKey: null,
  start: () => undefined,
  preview: () => undefined,
  drop: () => undefined,
  end: () => undefined,
});

function dashboardLink(path: string, onNavigate: Navigate) {
  return (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    onNavigate(path);
  };
}

function formatShort(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '—';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function statusName(value: string) {
  return ({
    NEW: 'Новая',
    DISTRIBUTION: 'На распределении',
    IN_PROGRESS: 'В работе',
    WAITING_CLIENT: 'Ожидает клиента',
    WAITING_APPROVAL: 'Ожидает согласования',
    RESOLVED: 'Решена',
    CLOSED: 'Закрыта',
  } as Record<string, string>)[value] ?? value;
}

function priorityName(value: string) {
  return ({
    CRITICAL: 'Критический',
    HIGH: 'Высокий',
    NORMAL: 'Обычный',
    LOW: 'Низкий',
  } as Record<string, string>)[value] ?? value;
}

function impactName(value?: string | null) {
  return ({
    NONE: 'Без перерыва в работе',
    LOW: 'Незначительное влияние',
    MEDIUM: 'Возможны ограничения',
    HIGH: 'Сервис будет недоступен',
  } as Record<string, string>)[String(value || '').toUpperCase()] ?? 'Влияние не указано';
}

function money(value: number) {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
  }).format(value || 0);
}

function minutesLabel(value: number) {
  const minutes = Math.max(0, Number(value || 0));
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return hours ? `${hours} ч${rest ? ` ${String(rest).padStart(2, '0')} мин` : ''}` : `${rest} мин`;
}

function compactMinutes(value: number) {
  return value < 60 ? minutesLabel(value) : `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(value / 60)} ч`;
}

function worklogPeriodLabel(value: string) {
  const [year, month] = String(value || '').split('-').map(Number);
  if (!year || !month) return 'Текущий месяц';
  const label = new Intl.DateTimeFormat('ru-RU', { month: 'long', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(Date.UTC(year, month - 1, 1)))
    .replace(/\s*г\.$/u, '');
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function plannedProgress(item: DashboardPlannedWork) {
  const total = Math.max(0, Number(item.checklist_total || 0));
  const completed = Math.min(total, Math.max(0, Number(item.checklist_completed || 0)));
  return total ? `${completed} из ${total}` : 'Без чек-листа';
}

function RoleCard({
  title,
  caption,
  icon,
  action,
  widgetKey,
  size = 'standard',
  className = '',
  children,
}: {
  title: string;
  caption?: string;
  icon: ReactNode;
  action?: ReactNode;
  widgetKey?: DashboardWidgetKey;
  size?: 'compact' | 'standard' | 'tall';
  className?: string;
  children: ReactNode;
}) {
  const editor = useContext(LayoutEditorContext);
  const dragBlockedRef = useRef(false);
  const position = widgetKey ? editor.orderedKeys.indexOf(widgetKey) : -1;
  const sortable = Boolean(widgetKey) && position >= 0;
  const beginDrag = (event: ReactDragEvent<HTMLElement>) => {
    if (!widgetKey || editor.saving || dragBlockedRef.current) {
      event.preventDefault();
      dragBlockedRef.current = false;
      return;
    }
    editor.start(widgetKey, event.dataTransfer);
  };
  return <Card
    as="section"
    className={`dashboard-role-card dashboard-role-card--reference dashboard-role-size--${size} ${editor.draggedKey === widgetKey ? 'is-layout-dragging ' : ''}${editor.dropKey === widgetKey ? 'is-layout-drop-target ' : ''}${className}`.trim()}
    data-card-treatment="floating"
    data-dashboard-widget={widgetKey}
    data-dashboard-size={size}
    data-dashboard-position={position >= 0 ? position + 1 : undefined}
    data-dashboard-sortable={sortable || undefined}
    draggable={sortable && !editor.saving}
    onPointerDownCapture={event => {
      const origin = event.target instanceof Element ? event.target : null;
      dragBlockedRef.current = Boolean(origin?.closest('a, button, input, select, textarea, [contenteditable="true"]'));
    }}
    onPointerUpCapture={() => { dragBlockedRef.current = false; }}
    onDragStart={beginDrag}
    onDragOver={event => {
      if (!widgetKey || editor.saving || !editor.draggedKey) return;
      event.preventDefault();
      event.stopPropagation();
      event.dataTransfer.dropEffect = 'move';
      const sourceIndex = editor.orderedKeys.indexOf(editor.draggedKey);
      const targetIndex = editor.orderedKeys.indexOf(widgetKey);
      const grid = event.currentTarget.parentElement;
      const source = grid
        ? dashboardCards(grid).find(card => card.dataset.dashboardWidget === editor.draggedKey)
        : undefined;
      if (!source || !shouldPreviewDashboardTarget(
        sourceIndex,
        targetIndex,
        { x: event.clientX, y: event.clientY },
        source.getBoundingClientRect(),
        event.currentTarget.getBoundingClientRect(),
      )) return;
      editor.preview(widgetKey);
    }}
    onDrop={event => {
      if (!widgetKey || editor.saving || !editor.draggedKey) return;
      event.preventDefault();
      event.stopPropagation();
      editor.drop();
    }}
    onDragEnd={editor.end}
  >
    {sortable && widgetKey &&
      <span
        className="dashboard-card-drag-handle"
        data-dashboard-drag-handle
        aria-hidden="true"
      ><GripVertical /></span>}
    <header className="dashboard-role-card__head">
      <div className="dashboard-role-card__title"><span className="dashboard-role-card__icon-badge" aria-hidden="true">{icon}</span><div><h2>{title}</h2>{caption && <p>{caption}</p>}</div></div>
      {action && <div className="dashboard-role-card__action">{action}</div>}
    </header>
    <div className="dashboard-role-card__body">{children}</div>
  </Card>;
}

function DashboardGrid({ className, enabled, layoutKey, children }: { className: string; enabled: boolean; layoutKey: string; children: ReactNode }) {
  const gridRef = useRef<HTMLDivElement | null>(null);
  const previousLayoutRef = useRef<{ grid: DOMRect; cards: Map<string, DOMRect> } | null>(null);
  const editor = useContext(LayoutEditorContext);

  useLayoutEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const items = dashboardCards(grid);
    const gridRect = grid.getBoundingClientRect();
    const current = new Map<string, DOMRect>();
    items.forEach(card => {
      const key = card.dataset.dashboardWidget;
      if (key) current.set(key, card.getBoundingClientRect());
    });
    const previous = previousLayoutRef.current;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    if (previous && !reduceMotion) {
      const gridShiftX = previous.grid.left - gridRect.left;
      const gridShiftY = previous.grid.top - gridRect.top;
      items.forEach(card => {
        if (card.classList.contains('is-layout-dragging') || typeof card.animate !== 'function') return;
        const key = card.dataset.dashboardWidget;
        const before = key ? previous.cards.get(key) : undefined;
        const after = key ? current.get(key) : undefined;
        if (!before || !after) return;
        const deltaX = before.left - after.left - gridShiftX;
        const deltaY = before.top - after.top - gridShiftY;
        if (Math.abs(deltaX) < 1 && Math.abs(deltaY) < 1) return;
        card.getAnimations?.().find(animation => animation.id === 'dashboard-card-reflow')?.cancel();
        const animation = card.animate([
          { transform: `translate3d(${deltaX}px, ${deltaY}px, 0)` },
          { transform: 'translate3d(0, 0, 0)' },
        ], {
          duration: 260,
          easing: 'cubic-bezier(.22, 1, .36, 1)',
        });
        if (animation) animation.id = 'dashboard-card-reflow';
      });
    }
    previousLayoutRef.current = { grid: gridRect, cards: current };
  }, [layoutKey]);

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return undefined;
    let timer = 0;
    let observer: ResizeObserver | undefined;
    let mutationObserver: MutationObserver | undefined;
    const reset = () => {
      grid.classList.remove('dashboard-role-grid--masonry');
      dashboardCards(grid).forEach(card => card.style.removeProperty('grid-row-end'));
    };
    const layout = () => {
      timer = 0;
      if (!enabled || window.innerWidth <= 1040) {
        reset();
        return;
      }
      grid.classList.add('dashboard-role-grid--masonry');
      const style = window.getComputedStyle(grid);
      const rowGap = Number.parseFloat(style.rowGap) || 18;
      const rowHeight = Number.parseFloat(style.gridAutoRows) || DASHBOARD_MASONRY_ROW_PX;
      const items = dashboardCards(grid);
      items.forEach(card => {
        const span = dashboardMasonrySpan(card.getBoundingClientRect().height, rowGap, rowHeight);
        card.style.gridRowEnd = `span ${span}`;
      });
    };
    const schedule = () => {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(layout, 0);
    };

    schedule();
    window.addEventListener('resize', schedule);
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(schedule);
      observer.observe(grid);
      dashboardCards(grid).forEach(card => observer?.observe(card));
    }
    if (typeof MutationObserver !== 'undefined') {
      mutationObserver = new MutationObserver(() => {
        dashboardCards(grid).forEach(card => observer?.observe(card));
        schedule();
      });
      mutationObserver.observe(grid, { childList: true });
    }
    return () => {
      if (timer) window.clearTimeout(timer);
      observer?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener('resize', schedule);
      reset();
    };
  }, [enabled]);

  return <div
    ref={gridRef}
    className={`dashboard-role-grid ${className}`}
    data-dashboard-layout="premium-4k"
    data-masonry-layout="adaptive"
    onDragOver={event => {
      if (event.target !== event.currentTarget || !editor.draggedKey || editor.saving) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      const items = dashboardCards(event.currentTarget);
      const bottom = Math.max(...items.map(card => card.getBoundingClientRect().bottom));
      const lastKey = editor.orderedKeys.at(-1);
      if (lastKey && Number.isFinite(bottom) && event.clientY > bottom + 8) editor.preview(lastKey);
    }}
    onDrop={event => {
      if (event.target !== event.currentTarget || !editor.draggedKey || editor.saving) return;
      event.preventDefault();
      editor.drop();
    }}
  >
    {children}
  </div>;
}

function ContextLink({ path, label, onNavigate }: { path: string; label: string; onNavigate: Navigate }) {
  return <a className="dashboard-role-link" href={path} onClick={dashboardLink(path, onNavigate)}>{label}<ChevronRight /></a>;
}

function CardActionLink({ path, label, onNavigate }: { path: string; label: string; onNavigate: Navigate }) {
  return <a className="sh-link" href={path} aria-label={label} title={label} onClick={dashboardLink(path, onNavigate)}><ArrowUpRight /></a>;
}

function InlineStatus({ tone, children }: { tone: 'ok' | 'warning' | 'danger' | 'info' | 'neutral'; children: ReactNode }) {
  return <span className={`dashboard-role-status dashboard-role-status--${tone}`}><i />{children}</span>;
}

function portalStatus(status?: MonitoringStatus): { tone: 'ok' | 'warning' | 'danger' | 'neutral'; label: string } {
  if (status === 'HEALTHY') return { tone: 'ok', label: 'Работает' };
  if (status === 'DEGRADED') return { tone: 'warning', label: 'Есть отклонения' };
  if (status === 'DOWN') return { tone: 'danger', label: 'Недоступен' };
  return { tone: 'neutral', label: 'Нет данных' };
}

function formatPercent(value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'Собираем';
  return `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(value)}%`;
}

function formatGib(value: number) {
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(Math.max(0, value) / (1024 ** 3));
}

function formatNetworkRate(bytesPerSecond: number | null | undefined) {
  if (bytesPerSecond === null || bytesPerSecond === undefined || !Number.isFinite(bytesPerSecond)) return '—';
  const bitsPerSecond = Math.max(0, bytesPerSecond) * 8;
  const scales = [
    { threshold: 1_000_000_000, divisor: 1_000_000_000, unit: 'Гбит/с' },
    { threshold: 1_000_000, divisor: 1_000_000, unit: 'Мбит/с' },
    { threshold: 1_000, divisor: 1_000, unit: 'Кбит/с' },
  ];
  const scale = scales.find(item => bitsPerSecond >= item.threshold);
  const value = scale ? bitsPerSecond / scale.divisor : bitsPerSecond;
  const unit = scale?.unit ?? 'бит/с';
  return `${new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(value)} ${unit}`;
}

function coreCountLabel(value: number) {
  const count = Math.max(0, Math.round(value));
  const lastTwo = count % 100;
  const last = count % 10;
  const suffix = last === 1 && lastTwo !== 11 ? 'ядро' : last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14) ? 'ядра' : 'ядер';
  return `${count} ${suffix}`;
}

function serverMetricTone(value: number | null | undefined) {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'pending';
  if (value >= 90) return 'danger';
  if (value >= 75) return 'warning';
  return 'ok';
}

function ServerMetric({ kind, label, percent, detail }: {
  kind: 'cpu' | 'memory' | 'disk';
  label: string;
  percent: number | null;
  detail: string;
}) {
  const ringValue = percent === null || !Number.isFinite(percent) ? 0 : Math.min(100, Math.max(0, percent));
  return <article className={`server-metric server-metric--${kind} is-${serverMetricTone(percent)}`} data-server-metric={kind}>
    <div className="server-ring" data-server-ring>
      <svg viewBox="0 0 42 42" aria-hidden="true"><circle cx="21" cy="21" r="16"/><circle cx="21" cy="21" r="16" pathLength="100" strokeDasharray={`${ringValue} 100`}/></svg>
      <strong>{formatPercent(percent)}</strong>
    </div>
    <span><b>{label}</b><small>{detail}</small></span>
  </article>;
}

function ServerNetwork({ metrics, observedAt }: {
  metrics?: ServerMetricsSnapshot['network'];
  observedAt?: string | null;
}) {
  const [clock, setClock] = useState(Date.now);
  const [history, setHistory] = useState<NetworkSample[]>(() => {
    try {
      const stored = JSON.parse(window.sessionStorage.getItem('ihd-server-network-history') || '[]') as NetworkSample[];
      return Array.isArray(stored)
        ? stored.filter(sample => Date.parse(sample.observedAt) >= Date.now() - 60_000 && Number.isFinite(sample.receive) && Number.isFinite(sample.transmit)).slice(-12)
        : [];
    } catch {
      return [];
    }
  });
  const observedAtRef = useRef<string | null>(history.at(-1)?.observedAt ?? null);
  const available = metrics?.available !== false;
  const hasRate = available
    && metrics?.receiveBytesPerSecond !== null
    && metrics?.receiveBytesPerSecond !== undefined
    && Number.isFinite(metrics.receiveBytesPerSecond)
    && metrics?.transmitBytesPerSecond !== null
    && metrics?.transmitBytesPerSecond !== undefined
    && Number.isFinite(metrics.transmitBytesPerSecond);
  const sampleTime = Date.parse(observedAt || '');
  const now = Math.max(clock, Date.now());
  const fresh = Number.isFinite(sampleTime) && sampleTime >= now - 15_000 && sampleTime <= now + 5_000;
  const stateLabel = !available ? 'Источник недоступен' : hasRate ? fresh ? 'В реальном времени' : 'Нет свежих замеров' : 'Первый замер';

  useEffect(() => {
    if (!hasRate) return;
    setClock(Date.now());
    const timer = window.setInterval(() => setClock(Date.now()), 5_000);
    return () => window.clearInterval(timer);
  }, [hasRate]);

  useEffect(() => {
    if (!metrics?.available) return;
    const receive = metrics.receiveBytesPerSecond;
    const transmit = metrics.transmitBytesPerSecond;
    if (receive === null || transmit === null || !Number.isFinite(receive) || !Number.isFinite(transmit) || !observedAt) return;
    if (observedAtRef.current === observedAt) return;
    observedAtRef.current = observedAt;
    setHistory(current => {
      const next = networkWindow([...current, { observedAt, receive, transmit }]).samples;
      try { window.sessionStorage.setItem('ihd-server-network-history', JSON.stringify(next)); } catch { /* protected browser storage */ }
      return next;
    });
  }, [metrics?.available, metrics?.receiveBytesPerSecond, metrics?.transmitBytesPerSecond, observedAt]);

  return <article
    className="server-network"
    data-server-network
    data-network-state={!available ? 'unavailable' : hasRate ? fresh ? 'live' : 'stale' : 'collecting'}
  >
    <div className="server-network__identity">
      <span className="server-network__icon" aria-hidden="true"><Network /></span>
      <span><strong>Сеть</strong><small>{metrics?.interface || 'Основной интерфейс'}</small></span>
      <span className="server-network__live"><i />{stateLabel}</span>
    </div>
    <div className="server-network__rates">
      <span data-network-rate="receive"><small><ArrowDown />Загрузка</small><strong>{formatNetworkRate(metrics?.receiveBytesPerSecond)}</strong></span>
      <span data-network-rate="transmit"><small><ArrowUp />Передача</small><strong>{formatNetworkRate(metrics?.transmitBytesPerSecond)}</strong></span>
      {!available && <p>Данные сети недоступны</p>}
      {available && !hasRate && <p>Собираем первый замер</p>}
    </div>
    <NetworkPlot history={history} />
  </article>;
}

function SystemHealthCard({ monitoring, monitoringState, onNavigate, onRetryMonitoring }: Pick<DashboardRoleViewsProps,
  'monitoring' | 'monitoringState' | 'onNavigate' | 'onRetryMonitoring'
>) {
  const [tab, setTab] = useState<'now' | 'day' | 'protection'>('now');
  const components = monitoring?.components ?? [];
  const monitoredComponents = components.filter(component => !(component.status === 'UNKNOWN' && !component.actionable));
  const counts = monitoring?.counts ?? { healthy: 0, degraded: 0, down: 0, unknown: 0, activeIncidents: 0 };
  const healthy = monitoredComponents.filter(component => component.status === 'HEALTHY').length;
  const deviations = monitoredComponents.filter(component => component.status === 'DEGRADED' || component.status === 'UNKNOWN').length;
  const down = monitoredComponents.filter(component => component.status === 'DOWN').length;
  const total = monitoredComponents.length;
  const maxLatency = Math.max(0, ...monitoredComponents.map(component => Number(component.latencyMs) || 0));
  const percent = total ? Math.round((healthy / total) * 100) : 0;
  const presentation = portalStatus(monitoring?.status);
  const end = new Date(monitoring?.observedAt || Date.now()).getTime();
  const events = monitoring?.incidents ?? [];
  const hours = Array<number>(24).fill(0);
  events.forEach(incident => {
    const index = Math.max(0, 23 - Math.floor((end - Date.parse(incident.last_seen_at)) / 3_600_000));
    hours[index] = Math.max(hours[index], incident.severity === 'CRITICAL' ? 3 : incident.severity === 'WARNING' ? 2 : 1);
  });
  const occurrences = events.reduce((sum, incident) => sum + Math.max(1, Number(incident.occurrences) || 0), 0);
  const latestIncident = events[0];
  const checked = monitoring?.observedAt ? `Проверено ${formatShort(monitoring.observedAt)}` : 'Ожидаем данные';
  const protection = [['backups', 'Резервные копии'], ['security', 'Защита входа'], ['minio', 'Файловое хранилище']] as const;
  const tabs = [['now', 'Сервисы'], ['day', '24 часа'], ['protection', 'Защита']] as const;

  let panel: ReactNode;
  if (tab === 'now') panel = <>
    <div className="sh-overview">
      <div className={`sh-ring is-${presentation.tone}`} data-system-health-ring>
        <svg viewBox="0 0 42 42" aria-hidden="true"><circle cx="21" cy="21" r="16"/><circle cx="21" cy="21" r="16" pathLength="100" strokeDasharray={`${percent} 100`}/></svg>
        <span><strong>{healthy}/{total}</strong><small>исправны</small></span>
      </div>
      <div className="sh-state"><InlineStatus tone={presentation.tone}>{presentation.label}</InlineStatus></div>
    </div>
    <div className="sh-metrics">{[
      [`${deviations} ${deviations === 1 ? 'отклонение' : 'отклонений'}`, 'требуют проверки'],
      [`${down} ${down === 1 ? 'сбой' : 'сбоев'}`, 'недоступно'],
      [`${maxLatency} мс`, 'макс. отклик'],
    ].map(([value, label]) => <span key={label}><strong>{value}</strong><small>{label}</small></span>)}</div>
    <div className="sh-services">
      {components.map(component => {
        const state = component.status === 'UNKNOWN' && !component.actionable
          ? { tone: 'neutral' as const, label: 'Не настроен' }
          : portalStatus(component.status);
        return <article key={component.key} data-portal-service={component.key} data-tone={state.tone} title={component.summary}>
          <i/>
          <span><strong>{component.label}</strong><small>{state.label}</small></span>
          <b>{component.latencyMs} мс</b>
        </article>;
      })}
      {!components.length && <span className="sh-services__empty">Собираем состояние сервисов</span>}
    </div>
  </>;
  else if (tab === 'day') panel = <>
    <div className="sh-day-stats">{[[events.length, 'События за 24 часа'], [counts.activeIncidents, 'Активны'], [occurrences, 'Срабатывания']].map(([value, label]) => <span key={label}><strong>{value}</strong><small>{label}</small></span>)}</div>
    <div className="sh-timeline">{hours.map((rank, index) => <i key={index} data-tone={['ok', 'info', 'warning', 'critical'][rank]}/>)}</div>
    <div className={`sh-latest${latestIncident ? '' : ' is-ok'}`}>{latestIncident ? <span data-tone={latestIncident.severity.toLowerCase()}/> : <CheckCircle2/>}<div><strong>{latestIncident?.title || 'Событий не было'}</strong><small>{latestIncident ? `${latestIncident.occurrences} ${latestIncident.occurrences === 1 ? 'срабатывание' : 'срабатывания'}` : 'Без отклонений'}</small></div></div>
  </>;
  else panel = <div className="sh-protection">{protection.map(([key, label]) => {
    const component = components.find(item => item.key === key);
    const verify = String(component?.details.deepVerifyStatus || '');
    const detail = key === 'backups' ? `Проверка восстановления: ${verify === 'OK' ? 'пройдена' : verify === 'FAILED' ? 'ошибка' : 'нет данных'}` : component?.summary || 'Ожидаем данные';
    return <article key={key} data-tone={portalStatus(component?.status).tone}><div><strong>{label}</strong><small>{detail}</small></div><i/></article>;
  })}</div>;

  return <RoleCard
    widgetKey="system_health"
    title="Состояние портала"
    caption={monitoringState === 'error' ? 'Нет данных' : checked}
    icon={<ShieldCheck />}
    size="compact"
    className="dashboard-role-card--system-health dashboard-role-span-4"
    action={<CardActionLink path="/monitoring" label="Открыть мониторинг" onNavigate={onNavigate}/>}
  >
    <div className="sh" data-system-health-card>
      <div className="sh-tabs" role="tablist">
        {tabs.map(([key, label]) => <button key={key} type="button" role="tab" aria-selected={tab === key} data-system-health-tab={key} onClick={() => setTab(key)}>{label}</button>)}
      </div>
      {monitoringState === 'error' ? <div className="dashboard-role-diagnostic-error sh-error" role="alert"><AlertTriangle/><strong>Не удалось получить состояние портала</strong><Button size="compact" onClick={onRetryMonitoring}>Повторить проверку</Button></div> : <section className="sh-panel" role="tabpanel" data-system-health-panel={tab}>{panel}</section>}
    </div>
  </RoleCard>;
}

function ServerCard({ serverMetrics, serverMetricsState, onRetryServerMetrics }: Pick<DashboardRoleViewsProps,
  'serverMetrics' | 'serverMetricsState' | 'onRetryServerMetrics'
>) {
  const updated = serverMetrics?.observedAt
    ? `Обновлено ${new Date(serverMetrics.observedAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`
    : serverMetricsState === 'error' ? 'Нет данных' : 'Получаем данные';
  const memoryDetail = serverMetrics
    ? `${formatGib(serverMetrics.memory.usedBytes)} из ${formatGib(serverMetrics.memory.totalBytes)} ГБ`
    : 'Использование памяти';
  const diskDetail = serverMetrics
    ? `${formatGib(serverMetrics.disk.usedBytes)} из ${formatGib(serverMetrics.disk.totalBytes)} ГБ`
    : 'Корневой раздел';

  return <RoleCard
    widgetKey="server"
    title="Сервер"
    caption={updated}
    icon={<Server />}
    size="compact"
    className="dashboard-role-card--server dashboard-role-span-4"
  >
    {serverMetricsState === 'error'
      ? <div className="dashboard-role-diagnostic-error server-error" role="alert"><AlertTriangle/><strong>Метрики сервера недоступны</strong><Button size="compact" onClick={onRetryServerMetrics}>Повторить</Button></div>
      : <div className="server-metrics" data-server-card>
        <ServerMetric kind="cpu" label="ЦП" percent={serverMetrics?.cpu.usagePercent ?? null} detail={serverMetrics ? coreCountLabel(serverMetrics.cpu.coreCount) : 'Собираем первый замер'}/>
        <ServerMetric kind="memory" label="RAM" percent={serverMetrics?.memory.usagePercent ?? null} detail={memoryDetail}/>
        <ServerMetric kind="disk" label="Диск" percent={serverMetrics?.disk.usagePercent ?? null} detail={diskDetail}/>
        <ServerNetwork metrics={serverMetrics?.network} observedAt={serverMetrics?.observedAt}/>
      </div>}
  </RoleCard>;
}

function EmptyMessage({ icon, title, description, action, stateKey }: {
  icon?: ReactNode;
  title: string;
  description: string;
  action?: ReactNode;
  stateKey?: string;
}) {
  return <div className="dashboard-role-empty" data-empty-state={stateKey}>
    <span className="dashboard-role-empty__icon" aria-hidden="true">{icon || <CheckCircle2 />}</span>
    <span className="dashboard-role-empty__copy"><strong>{title}</strong><small>{description}</small></span>
    {action}
  </div>;
}

function BrandMark({ brand }: { brand: 'beeline' | 'telegram' | 'max' | 'bitrix' | 'mail' }) {
  if (brand === 'max') return <span className="dashboard-brand dashboard-brand--max" data-brand="max"><img src="/channel-icons/max.png" alt="" /></span>;
  if (brand === 'telegram') return <span className="dashboard-brand dashboard-brand--telegram" data-brand="telegram"><svg viewBox="0 0 48 48" aria-hidden="true"><circle cx="24" cy="24" r="23" fill="#2AABEE"/><path fill="#fff" d="m11.4 23.2 23.3-9c1.1-.4 2 .3 1.7 1.8l-4 18.8c-.3 1.3-1.1 1.6-2.2 1l-6.1-4.5-3 2.9c-.3.3-.6.6-1.2.6l.4-6.2 11.4-10.3c.5-.4-.1-.7-.8-.3L16.8 26.9l-6.1-1.9c-1.3-.4-1.4-1.3.7-1.8Z"/></svg></span>;
  if (brand === 'beeline') return <span className="dashboard-brand dashboard-brand--beeline" data-brand="beeline"><img src="/brand/beeline.svg" alt="" /></span>;
  if (brand === 'bitrix') return <span className="dashboard-brand dashboard-brand--bitrix" data-brand="bitrix"><img src="/brand/bitrix24.png" alt="" /></span>;
  return <span className="dashboard-brand dashboard-brand--mail" data-brand="mail"><Mail /></span>;
}

function ServiceMark({ service }: { service: 'freeswitch' | 'browser' }) {
  return <span className={`dashboard-service-mark dashboard-service-mark--${service}`} data-service={service} aria-hidden="true">
    {service === 'freeswitch' ? <Server /> : <PhoneCall />}
  </span>;
}

function MetricTiles({
  items,
  ariaLabel,
}: {
  items: Array<{ key?: string; label: string; value: number | string; note: string; icon: ReactNode; tone: string }>;
  ariaLabel: string;
}) {
  return <div className="dashboard-role-metrics" aria-label={ariaLabel} data-summary-metrics>
    {items.map(item => <article className={`dashboard-role-metric dashboard-role-tone--${item.tone}`} data-summary-metric={item.key || item.label} key={item.label}>
      <span aria-hidden="true">{item.icon}</span><div><small>{item.label}</small><strong>{item.value}</strong><em>{item.note}</em></div>
    </article>)}
  </div>;
}

const STATUS_PERIOD_OPTIONS: Array<{ value: DashboardStatusPeriod; label: string }> = [
  { value: 'today', label: 'Сегодня' },
  { value: '7d', label: '7 дней' },
  { value: 'month', label: 'Месяц' },
  { value: 'all', label: 'За всё время' },
];

function StatusOverview({ metrics, period, loading, onPeriodChange }: {
  metrics?: DashboardStatusMetrics | null;
  period: DashboardStatusPeriod;
  loading: boolean;
  onPeriodChange: (period: DashboardStatusPeriod) => void;
}) {
  const current = metrics?.period === period ? metrics : null;
  const statusItems = [
    { key: 'active', label: 'Активные', value: current?.active, tone: 'blue' },
    { key: 'in_progress', label: 'В работе', value: current?.in_progress, tone: 'green' },
    { key: 'waiting_client', label: 'Ожидают клиента', value: current?.waiting_client, tone: 'amber' },
    { key: 'resolved', label: 'Решено', value: current?.resolved, tone: 'cyan' },
    { key: 'unassigned', label: 'Без исполнителя', value: current?.unassigned, tone: 'orange' },
    { key: 'overdue', label: 'Просрочены', value: current?.overdue, tone: 'red' },
    { key: 'created', label: 'Создано', value: current?.created, tone: 'slate' },
  ];
  const maximum = Math.max(1, ...statusItems.map(item => item.value ?? 0));
  return <div className="dashboard-role-status-overview" data-status-overview data-status-view={period} aria-busy={loading}>
    <div className="dashboard-role-status-period" role="tablist" aria-label="Период статусов заявок">
      {STATUS_PERIOD_OPTIONS.map(option => <button type="button" role="tab" data-status-period={option.value} aria-selected={period === option.value} onClick={() => onPeriodChange(option.value)} key={option.value}>{option.label}</button>)}
    </div>
    <div className="dashboard-role-status-list">{statusItems.map(item => <div data-status-metric={item.key} data-tone={item.tone} key={item.key}>
      <i aria-hidden="true"/><span>{item.label}</span><strong>{item.value ?? '—'}</strong>
      <div className="dashboard-status-bar" aria-hidden="true"><b data-status-bar={item.key} style={{ width: `${(item.value ?? 0) / maximum * 100}%` }}/></div>
    </div>)}</div>
    <p className="dashboard-chart-note">Показатели пересекаются и не складываются в общий итог.</p>
  </div>;
}

function TicketTable({ tickets, onNavigate, label = 'recent' }: {
  tickets: DashboardTicket[];
  onNavigate: Navigate;
  label?: string;
}) {
  if (!tickets.length) return <EmptyMessage icon={<TicketIcon />} title="Заявок пока нет" description="Новые заявки появятся здесь" stateKey="tickets" action={<ContextLink path="/tickets" label="Открыть заявки" onNavigate={onNavigate}/>}/>;
  return <div className="dashboard-role-ticket-table" data-ticket-table={label}>
    <div className="dashboard-role-ticket-table__head" aria-hidden="true">
      <span>Номер</span><span>Тема</span><span>Компания / очередь</span><span>Статус</span><span>Приоритет</span><span>Изменено</span>
    </div>
    {tickets.map(ticket => {
      const path = `/tickets/${ticket.id}`;
      const statusTone = ticket.status === 'RESOLVED' || ticket.status === 'CLOSED' ? 'ok' : ticket.status.includes('WAITING') ? 'warning' : 'info';
      return <a href={path} onClick={dashboardLink(path, onNavigate)} className="dashboard-role-ticket-table__row" key={ticket.id}>
        <strong>{ticket.display_number}</strong>
        <span>{ticket.subject}</span>
        <span><b>{ticket.company_name || 'Компания не указана'}</b>{ticket.queue_name && <small>{ticket.queue_name}</small>}</span>
        <InlineStatus tone={statusTone}>{statusName(ticket.status)}</InlineStatus>
        <em className={`priority--${String(ticket.priority || 'NORMAL').toLowerCase()}`}>{priorityName(ticket.priority || 'NORMAL')}</em>
        <time>{formatShort(ticket.updated_at)}</time>
      </a>;
    })}
  </div>;
}

function AwaitingRows({ tickets, onNavigate }: { tickets: DashboardTicket[]; onNavigate: Navigate }) {
  if (!tickets.length) return <EmptyMessage icon={<MessageCircle />} title="Очередь ответов пуста" description="Сейчас ничего не требует ответа" stateKey="awaiting" action={<ContextLink path="/tickets" label="Открыть заявки" onNavigate={onNavigate}/>}/>;
  return <div className="dashboard-role-awaiting-list">{tickets.map(ticket => {
    const path = `/tickets/${ticket.id}`;
    return <a href={path} onClick={dashboardLink(path, onNavigate)} key={ticket.id}>
      <span className="dashboard-role-awaiting-list__title"><i/><strong>{ticket.display_number} · {ticket.subject}</strong></span>
      <span className="dashboard-role-awaiting-list__meta"><MessageCircle/><small>{statusName(ticket.status)}</small></span>
      <span className="dashboard-role-awaiting-list__footer"><time>{formatShort(ticket.updated_at)}</time><b>Ответить <ChevronRight/></b></span>
    </a>;
  })}</div>;
}

function SlaRows({ tickets, onNavigate }: { tickets: DashboardTicket[]; onNavigate: Navigate }) {
  if (!tickets.length) return <EmptyMessage icon={<ShieldCheck />} title="SLA в норме" description="Рисков SLA сейчас нет" stateKey="sla" action={<ContextLink path="/tickets" label="Открыть заявки" onNavigate={onNavigate}/>}/>;
  return <div className="dashboard-role-sla-table">
    <div className="dashboard-role-sla-table__head" aria-hidden="true"><span>Заявка</span><span>Срок SLA</span><span>Риск</span></div>
    {tickets.map(ticket => {
      const path = `/tickets/${ticket.id}`;
      const dangerous = ticket.priority === 'CRITICAL' || ticket.priority === 'HIGH';
      return <a href={path} onClick={dashboardLink(path, onNavigate)} key={ticket.id}>
        <span><strong>{ticket.display_number}</strong><small>{ticket.subject}</small></span>
        <time><Clock3/>{formatShort(ticket.resolution_due_at || ticket.updated_at)}</time>
        <em className={dangerous ? 'danger' : 'warning'}><AlertTriangle/>{dangerous ? 'Высокий риск' : 'Требует внимания'}</em>
      </a>;
    })}
  </div>;
}

function PlannedRows({ data, onNavigate }: { data: DashboardPlannedWork[]; onNavigate: Navigate }) {
  if (!data.length) return <EmptyMessage
    icon={<CalendarClock />}
    title="Расписание свободно"
    description="Ближайших плановых работ нет"
    stateKey="planned"
  />;
  return <div className="dashboard-role-planned-list">{data.slice(0, 3).map(item => {
    const path = `/planned-works?work=${item.id}`;
    const progress = Math.max(0, Math.min(100, Number(item.progress_percent ?? (item.checklist_total ? (Number(item.checklist_completed || 0) / Number(item.checklist_total)) * 100 : 0))));
    return <a href={path} onClick={dashboardLink(path, onNavigate)} key={item.id}>
      <span className="dashboard-role-planned-list__title"><CalendarClock/><span><strong>{item.title}</strong><small>{item.category || item.queue_name || 'Плановые работы'}</small></span></span>
      <span className="dashboard-role-planned-list__meta">
        <span><small>Дата и время</small><strong>{formatShort(item.scheduled_start)}</strong></span>
        <span><small>Влияние на работу</small><strong>{impactName(item.impact_level)}</strong></span>
        <span><small>Выполнение</small><strong>{plannedProgress(item)}</strong><i><b style={{ width: `${progress}%` }}/></i></span>
      </span>
    </a>;
  })}</div>;
}

function QueueRows({ data, limit = 5 }: { data: DashboardData['queues']; limit?: number }) {
  if (!data.length) return <div data-queue-table><EmptyMessage icon={<Inbox />} title="Очереди свободны" description="Активной нагрузки сейчас нет"/></div>;
  const max = Math.max(1, ...data.map(queue => queue.active));
  const visible = [...data]
    .sort((left, right) => right.overdue - left.overdue || right.active - left.active || left.name.localeCompare(right.name, 'ru'))
    .slice(0, limit);
  return <div className="dashboard-role-queue-table" data-queue-table>
    <div className="dashboard-role-queue-table__head" data-queue-table-header>
      <span>Очередь</span><span>Активные</span><span>Новые</span><span>Просрочено</span><span>Загрузка</span>
    </div>
    <div className="dashboard-role-queues">{visible.map(queue => {
      const load = Math.round((queue.active / max) * 100);
      return <article className="dashboard-role-queue-row" key={queue.id} role="meter" aria-label={`${queue.name}: ${queue.active}`} aria-valuemin={0} aria-valuemax={max} aria-valuenow={queue.active}>
        <strong>{queue.name}</strong><span>{queue.active}</span><span>{queue.new_count}</span><span>{queue.overdue}</span>
        <span className="dashboard-role-queue-row__load"><i><b style={{ width: `${Math.max(queue.active ? 5 : 0, load)}%`, backgroundColor: queue.color || 'var(--ui-color-info)' }}/></i><em>{load}%</em></span>
      </article>;
    })}</div>
  </div>;
}

export function TrendCard({ trend, period, loading, fullWidth = false, onPeriodChange }: {
  trend?: DashboardTrendData | null;
  period: DashboardTrendPeriod;
  loading: boolean;
  fullWidth?: boolean;
  onPeriodChange: (period: DashboardTrendPeriod) => void;
}) {
  const [series, setSeries] = useState({ created: true, resolved: true });
  const totals = (trend?.points || []).reduce((sum, point) => ({ created: sum.created + point.created, resolved: sum.resolved + point.resolved }), { created: 0, resolved: 0 });
  return <RoleCard widgetKey="trend" title="Динамика обращений" icon={<BarChart3 />} size="compact" className={`${fullWidth ? 'dashboard-role-span-12' : 'dashboard-role-span-6'} dashboard-role-trend`} action={<label><span className="sr-only">Период динамики</span><select value={period} onChange={event => onPeriodChange(event.target.value as DashboardTrendPeriod)}>{TREND_OPTIONS.map(option => <option value={option.value} key={option.value}>{option.label}</option>)}</select></label>}>
    <div className="dashboard-role-trend__legend">{(['created', 'resolved'] as const).map(key => <button type="button" data-trend-series={key} key={key} aria-pressed={series[key]} onClick={() => setSeries(current => ({ ...current, [key]: !current[key] }))}><i className={key} /><small>{key === 'created' ? 'Поступило' : 'Решено'}</small><strong>{totals[key]}</strong></button>)}</div>
    <TicketTrendPlot trend={trend} period={period} loading={loading} series={series}/>
  </RoleCard>;
}

function WorkloadRows({ data }: { data: DashboardData['staff_workload'] }) {
  if (!data.length) return <EmptyMessage icon={<Users />} title="Активной нагрузки нет" description="Данные появятся после назначения заявок"/>;
  const max = Math.max(1, ...data.map(person => person.active));
  return <div className="dashboard-role-workload">{data.slice(0, 5).map(person => <div key={person.user_id}>
    <span><i aria-hidden="true">{person.full_name.split(/\s+/).map(part => part[0]).join('').slice(0, 2).toUpperCase()}</i><strong>{person.full_name}</strong><em>{person.active} активных</em></span>
    <i><b style={{ width: `${Math.max(person.active ? 5 : 0, (person.active / max) * 100)}%` }} /></i>
    <small className={person.overdue ? 'danger' : ''}>{person.overdue ? `${person.overdue} просрочено` : 'Сроки соблюдаются'}</small>
  </div>)}</div>;
}

export function WorklogSummary({ data, showAmount }: { data: DashboardData; showAmount: boolean }) {
  const [activeWeek, setActiveWeek] = useState<number | null>(null);
  const worklogs = data.worklogs ?? {
    period_month: '',
    billable_minutes: data.metrics.month_minutes,
    internal_minutes: 0,
    uncategorized_minutes: 0,
    weekly: [],
  };
  const hasWorklogs = data.metrics.month_minutes > 0 || (showAmount && data.metrics.month_amount > 0);
  const breakdown = [
    { key: 'billable', label: 'Оплачиваемое', minutes: worklogs.billable_minutes, tone: 'cobalt' },
    { key: 'internal', label: 'Внутреннее', minutes: worklogs.internal_minutes, tone: 'cyan' },
    { key: 'uncategorized', label: 'Без категории', minutes: worklogs.uncategorized_minutes, tone: 'slate' },
  ];
  const classifiedMinutes = Math.max(1, breakdown.reduce((sum, item) => sum + item.minutes, 0));
  const billableShare = worklogs.billable_minutes / classifiedMinutes * 100;
  const internalShare = worklogs.internal_minutes / classifiedMinutes * 100;
  const uncategorizedShare = worklogs.uncategorized_minutes / classifiedMinutes * 100;
  const weekly = worklogs.weekly.length
    ? worklogs.weekly
    : [1, 2, 3, 4, 5].map(week => ({ week, billable_minutes: 0, internal_minutes: 0, uncategorized_minutes: 0 }));
  const weekTotals = weekly.map(item => item.billable_minutes + item.internal_minutes + item.uncategorized_minutes);
  const peakWeek = Math.max(0, ...weekTotals);
  const maxWeek = Math.max(1, peakWeek);
  const selectedWeek = weekly.find(item => item.week === activeWeek);

  return <div className="dashboard-role-worklog" data-worklog-card="premium" data-empty-state={hasWorklogs ? undefined : 'worklogs'} data-show-amount={showAmount || undefined}>
    <div className="dashboard-role-worklog__overview">
      <div className="dashboard-role-worklog__primary">
        <span>Учтённое время <CircleHelp aria-hidden="true" /></span>
        <strong>{minutesLabel(data.metrics.month_minutes)}</strong>
        {!hasWorklogs && <em><strong>Время пока не учтено</strong></em>}
        <small>{hasWorklogs ? 'Всего за текущий месяц' : 'Записи за текущий месяц ещё не добавлены'}</small>
      </div>
      {showAmount && <div className="dashboard-role-worklog__primary dashboard-role-worklog__primary--amount">
        <span>К расчёту <CircleHelp aria-hidden="true" /></span>
        <strong>{money(data.metrics.month_amount)}</strong>
        <small>По закрытым расчётам месяца</small>
      </div>}
      <div className="dashboard-role-worklog__ratio" aria-label={`Оплачиваемое время: ${Math.round(billableShare)}%`}>
        <span className="dashboard-role-worklog__ring" aria-hidden="true">
          <svg viewBox="0 0 120 120" width="124" height="124">
            <circle className="track" cx="60" cy="60" r="49" pathLength="100" />
            <circle className="billable" cx="60" cy="60" r="49" pathLength="100" strokeDasharray={`${billableShare} ${100 - billableShare}`} />
            <circle className="internal" cx="60" cy="60" r="49" pathLength="100" strokeDasharray={`${internalShare} ${100 - internalShare}`} strokeDashoffset={-billableShare} />
            <circle className="uncategorized" cx="60" cy="60" r="49" pathLength="100" strokeDasharray={`${uncategorizedShare} ${100 - uncategorizedShare}`} strokeDashoffset={-(billableShare + internalShare)} />
          </svg>
          <b>{Math.round(billableShare)}%</b>
        </span>
        <span><strong>Оплачиваемое время</strong><small>{minutesLabel(worklogs.billable_minutes)} из {minutesLabel(data.metrics.month_minutes)}</small></span>
      </div>
    </div>

    <div className="dashboard-role-worklog__analytics">
      <div className="dashboard-role-worklog__breakdown" aria-label="Распределение трудозатрат">
        {breakdown.map(item => <div key={item.key} data-tone={item.tone}>
            <i className="dashboard-role-worklog__dot" aria-hidden="true" />
            <span><strong>{item.label}</strong></span>
            <b>{minutesLabel(item.minutes)}</b>
          </div>)}
      </div>

      <div className="dashboard-role-worklog__weekly" aria-label="Трудозатраты по неделям">
        <div className="dashboard-role-worklog__weekly-head"><span><strong>По неделям</strong><small>Распределение учтённого времени</small></span>{peakWeek > 0 && <b>{minutesLabel(peakWeek)} пик</b>}</div>
        <div className="dashboard-worklog-scale" aria-hidden="true"><span>{compactMinutes(peakWeek)}</span><span>0</span></div>
        <div className="dashboard-role-worklog__chart" style={{ gridTemplateColumns: `repeat(${weekly.length}, minmax(0, 1fr))` }}>
          <span className="dashboard-role-worklog__grid" aria-hidden="true"><i /><i /><i /></span>
          {weekly.map((item, index) => {
            const total = weekTotals[index];
            const height = total / maxWeek * 100;
            return <div className="dashboard-role-worklog__week" data-worklog-week={item.week} key={item.week} tabIndex={0} role="img" aria-label={`${item.week} неделя: ${minutesLabel(total)}`}
              onPointerEnter={() => setActiveWeek(item.week)} onPointerLeave={() => setActiveWeek(null)} onFocus={() => setActiveWeek(item.week)} onBlur={() => setActiveWeek(null)} onKeyDown={event => { if (event.key === 'Escape') setActiveWeek(null); }}>
              <b className="dashboard-worklog-value">{compactMinutes(total)}</b>
              <span className="dashboard-role-worklog__bar-track" aria-hidden="true">
                <i className="dashboard-role-worklog__bar" style={{ height: `${height}%` }}>
                  {!!item.billable_minutes && <b data-tone="cobalt" style={{ flexGrow: item.billable_minutes }} />}
                  {!!item.internal_minutes && <b data-tone="cyan" style={{ flexGrow: item.internal_minutes }} />}
                  {!!item.uncategorized_minutes && <b data-tone="slate" style={{ flexGrow: item.uncategorized_minutes }} />}
                </i>
              </span>
              <small>{item.week} нед</small>
            </div>;
          })}
        </div>
        <div className="dashboard-worklog-tooltip dashboard-plot-tooltip" role="status">{selectedWeek ? <><strong>{selectedWeek.week} неделя</strong><span>Оплачиваемое: {minutesLabel(selectedWeek.billable_minutes)}</span><span>Внутреннее: {minutesLabel(selectedWeek.internal_minutes)}</span><span>Без категории: {minutesLabel(selectedWeek.uncategorized_minutes)}</span></> : <span>Выберите неделю для подробностей</span>}</div>
      </div>
    </div>
  </div>;
}

function WorklogAction({ data, onNavigate }: { data: DashboardData; onNavigate: Navigate }) {
  return <div className="dashboard-role-worklog__actions">
    <span><CalendarClock aria-hidden="true" />{worklogPeriodLabel(data.worklogs?.period_month || '')}</span>
    <CardActionLink path="/billing" label="Открыть отчёт" onNavigate={onNavigate}/>
  </div>;
}

function ActivityRows({ data, onNavigate }: { data: DashboardData['activity']; onNavigate: Navigate }) {
  if (!data.length) return <EmptyMessage icon={<Activity />} title="Лента спокойна" description="Новых событий сейчас нет"/>;
  return <div className="dashboard-role-ticket-list">{data.slice(0, 4).map(item => {
    const path = `/tickets/${item.ticket_id}`;
    return <a href={path} onClick={dashboardLink(path, onNavigate)} className="dashboard-role-ticket is-activity" key={item.id}>
      <Activity />
      <span><strong>{item.display_number} · {item.subject}</strong><small>{item.author_name || 'Сотрудник'} · {item.body || item.event_type}</small></span>
      <time>{formatShort(item.occurred_at)}</time>
      <ChevronRight />
    </a>;
  })}</div>;
}

function ChannelSummary({ value, onNavigate }: { value: number; onNavigate: Navigate }) {
  return <div className="dashboard-role-channel-summary">
    <div><span aria-hidden="true"><MessageCircle/></span><strong>{value}</strong><small>Диалогов ожидают ответа</small></div>
    <ContextLink path="/contact-center?status=ACTIVE" label="Открыть контакт-центр" onNavigate={onNavigate}/>
  </div>;
}

function CallSummary({ value, onNavigate }: { value: number; onNavigate: Navigate }) {
  return <div className="dashboard-role-call-summary">
    <div><span aria-hidden="true"><PhoneMissed/></span><strong>{value}</strong><small>Необработанных вызовов</small></div>
    <ContextLink path="/telephony?section=CALLS" label="Открыть журнал звонков" onNavigate={onNavigate}/>
  </div>;
}

type IntegrationTile = {
  brand: 'telegram' | 'max' | 'bitrix' | 'mail';
  title: string;
  componentKey: 'telegram' | 'max' | 'bitrix24' | 'mail';
};

const INTEGRATION_TILES: IntegrationTile[] = [
  { brand: 'telegram', title: 'Telegram', componentKey: 'telegram' },
  { brand: 'max', title: 'MAX', componentKey: 'max' },
  { brand: 'bitrix', title: 'Битрикс24', componentKey: 'bitrix24' },
  { brand: 'mail', title: 'Почта', componentKey: 'mail' },
];

function integrationDetailNumber(value: unknown) {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(0, Math.round(number)) : 0;
}

function integrationDetailText(value: unknown) {
  return typeof value === 'string' && value.trim() ? value : null;
}

function integrationErrorLabel(value: number) {
  if (!value) return 'без ошибок';
  const lastTwo = value % 100;
  const last = value % 10;
  const noun = lastTwo >= 11 && lastTwo <= 14 ? 'ошибок' : last === 1 ? 'ошибка' : last >= 2 && last <= 4 ? 'ошибки' : 'ошибок';
  return `${value} ${noun}`;
}

function integrationPresentation(
  monitoring: MonitoringSnapshot | null | undefined,
  state: DiagnosticsState,
  componentKey: IntegrationTile['componentKey'],
) {
  const unavailable = (label: string, activity: string) => ({
    label,
    tone: 'neutral' as const,
    connected: false,
    activity,
    operations: 'Операционные данные недоступны',
  });
  if (state === 'loading') return unavailable('Проверяем', 'Обновляем сведения об обмене');
  if (state === 'error') return unavailable('Нет данных', 'Не удалось получить сведения об обмене');
  const component = monitoring?.components.find(item => item.key === componentKey);
  if (!component) return unavailable('Нет данных', 'Сведения об интеграции отсутствуют');
  const processed24h = integrationDetailNumber(component.details.processed24h);
  const errorCount = integrationDetailNumber(component.details.errorCount);
  const lastSuccessAt = integrationDetailText(component.details.lastSuccessAt);
  const syncAt = integrationDetailText(component.details.syncAt);
  const activity = lastSuccessAt ? `Последний обмен ${formatShort(lastSuccessAt)}` : 'Успешных обменов пока нет';
  const operations = `${processed24h} за 24 ч · ${integrationErrorLabel(errorCount)}${syncAt ? ` · Синхронизация ${formatShort(syncAt)}` : ''}`;
  if (component.status === 'HEALTHY') return { label: 'Работает', tone: 'ok' as const, connected: true, activity, operations };
  if (component.status === 'DEGRADED') return { label: 'Требует внимания', tone: 'warning' as const, connected: true, activity, operations };
  if (component.status === 'DOWN') return { label: 'Недоступен', tone: 'danger' as const, connected: true, activity, operations };
  return component.actionable
    ? unavailable('Нет данных', activity)
    : unavailable('Не настроен', 'Данные появятся после подключения');
}

function SupplementalWidget({ widgetKey, role, ...props }: DashboardRoleViewsProps & {
  widgetKey: DashboardWidgetKey;
  role: DashboardRole;
}) {
  const { data, trend, trendPeriod, trendLoading, statusMetrics, statusPeriod, statusLoading, onNavigate, onTrendPeriodChange, onStatusPeriodChange } = props;
  const waiting = data.awaiting ?? [];
  if (widgetKey === 'trend') return <TrendCard trend={trend} period={trendPeriod} loading={trendLoading} onPeriodChange={onTrendPeriodChange}/>;
  if (widgetKey === 'statuses') return <RoleCard widgetKey={widgetKey} title="Статусы заявок" caption="Состояние обращений, созданных за период" icon={<Gauge />} className="dashboard-role-span-5"><StatusOverview metrics={statusMetrics} period={statusPeriod} loading={statusLoading} onPeriodChange={onStatusPeriodChange}/></RoleCard>;
  if (widgetKey === 'services') return <RoleCard widgetKey={widgetKey} title="Очереди и направления" caption="Распределение текущей нагрузки" icon={<Inbox />} className="dashboard-role-span-7"><QueueRows data={data.queues}/></RoleCard>;
  if (widgetKey === 'recent') return <RoleCard widgetKey={widgetKey} title="Последние заявки" caption="Недавно назначенные и обновлённые обращения" icon={<TicketIcon />} className="dashboard-role-span-8" action={<CardActionLink path="/tickets" label="Открыть заявки" onNavigate={onNavigate}/>}><TicketTable tickets={data.recent.slice(0, 3)} onNavigate={onNavigate}/></RoleCard>;
  if (widgetKey === 'awaiting') return <RoleCard widgetKey={widgetKey} title="Требуют ответа" caption="Обращения, где ожидается действие" icon={<CircleDot />} className="dashboard-role-span-4"><AwaitingRows tickets={waiting.slice(0, 3)} onNavigate={onNavigate}/></RoleCard>;
  if (widgetKey === 'unread_contacts') return <RoleCard widgetKey={widgetKey} title="Непрочитанные диалоги" caption="Клиенты ожидают ответа в контакт-центре" icon={<MessageCircle />} className="dashboard-role-span-6" action={<InlineStatus tone={data.operational.unread_conversations ? 'warning' : 'ok'}>{data.operational.unread_conversations} новых</InlineStatus>}><ChannelSummary value={data.operational.unread_conversations} onNavigate={onNavigate}/></RoleCard>;
  if (widgetKey === 'missed_calls') return <RoleCard widgetKey={widgetKey} title="Пропущенные звонки" caption="Вызовы, требующие обратной связи" icon={<PhoneMissed />} className="dashboard-role-span-6"><CallSummary value={data.operational.missed_calls} onNavigate={onNavigate}/></RoleCard>;
  if (widgetKey === 'planned') return <RoleCard widgetKey={widgetKey} title="Плановые работы" caption="Ближайшие регламентные операции" icon={<CalendarClock />} className={`dashboard-role-span-6${data.planned_works.length ? '' : ' dashboard-role-card--empty-compact'}`}><PlannedRows data={data.planned_works} onNavigate={onNavigate}/></RoleCard>;
  if (widgetKey === 'activity') return <RoleCard widgetKey={widgetKey} title="Активность команды" caption="Последние события в заявках" icon={<Activity />} className="dashboard-role-span-6"><ActivityRows data={data.activity} onNavigate={onNavigate}/></RoleCard>;
  if (widgetKey === 'notices') return <RoleCard widgetKey={widgetKey} title="Важные уведомления" caption="Ситуации, которые требуют внимания" icon={<AlertTriangle />} className="dashboard-role-span-6"><MetricTiles ariaLabel="Важные уведомления" items={[
        { label: 'Просрочены', value: data.metrics.overdue, note: 'Нарушен срок', icon: <Clock3/>, tone: 'red' },
        { label: 'Без исполнителя', value: data.metrics.unassigned, note: 'Нужно назначить', icon: <Users/>, tone: 'amber' },
      ]}/></RoleCard>;
  if (widgetKey === 'sla') return <RoleCard widgetKey={widgetKey} title="SLA и риски" caption="Ближайшие и нарушенные сроки" icon={<Clock3 />} className="dashboard-role-span-6"><SlaRows tickets={data.risky.slice(0, 3)} onNavigate={onNavigate}/></RoleCard>;
  if (widgetKey === 'workload') return <RoleCard widgetKey={widgetKey} title="Загрузка сотрудников" caption="Активные и просроченные задачи" icon={<Users />} className="dashboard-role-span-6"><WorkloadRows data={data.staff_workload}/></RoleCard>;
  if (widgetKey === 'worklogs') return <RoleCard widgetKey={widgetKey} title="Трудозатраты" icon={<Timer />} size="compact" className="dashboard-role-span-6 dashboard-role-card--worklog" action={<WorklogAction data={data} onNavigate={onNavigate}/>}><WorklogSummary data={data} showAmount={role === 'admin' || role === 'manager'}/></RoleCard>;
  return null;
}

function AdminDashboard(props: DashboardRoleViewsProps) {
  const { data, diagnostics, diagnosticsState, monitoring, monitoringState, serverMetrics, serverMetricsState, onNavigate, onRetryMonitoring, onRetryServerMetrics } = props;
  const registered = diagnostics?.pbx?.registrationStatus === 'REGISTERED';
  const gateway = Boolean(diagnostics?.pbx?.gatewayReachable);
  const errors = Number(diagnostics?.webhook?.failed_recent || 0);
  const portalFailures = monitoring?.components.filter(component => component.status === 'DOWN' || component.status === 'DEGRADED') ?? [];
  const attentionTickets = data.attention_tickets ?? [];
  const ticketAlerts = attentionTickets.map(ticket => {
    const reasons = [ticket.is_overdue ? 'Просрочено' : null, ticket.is_unassigned ? 'Без исполнителя' : null].filter(Boolean);
    const context = ticket.queue_name || ticket.company_name;
    return {
      key: `ticket-${ticket.id}`,
      severity: ticket.is_overdue ? 7 : 5,
      badge: reasons.join(' · '),
      tone: ticket.is_overdue ? 'danger' as const : 'warning' as const,
      title: `${ticket.display_number} · ${ticket.subject}`,
      note: [reasons.join(' · '), context].filter(Boolean).join(' — '),
      path: `/tickets/${ticket.id}`,
    };
  });
  const serviceAlerts = portalFailures.map(component => ({
    key: `service-${component.key}`,
    severity: component.status === 'DOWN' ? 7 : 5,
    badge: component.status === 'DOWN' ? 'Сбой' : 'Проверить',
    tone: component.status === 'DOWN' ? 'danger' as const : 'warning' as const,
    title: component.label,
    note: component.summary,
    path: '/monitoring',
  }));
  const hasTelephonyServiceAlert = portalFailures.some(component => component.key === 'telephony');
  const fallbackTicketAlerts = attentionTickets.length ? [] : ([
    data.metrics.overdue > 0 ? { key: 'overdue-fallback', severity: 6, badge: String(data.metrics.overdue), tone: 'danger' as const, title: 'Просроченные заявки', note: 'Откройте список для разбора конкретных заявок', path: '/tickets?overdue=1' } : null,
    data.metrics.unassigned > 0 ? { key: 'unassigned-fallback', severity: 5, badge: String(data.metrics.unassigned), tone: 'warning' as const, title: 'Заявки без исполнителя', note: 'Откройте список для назначения ответственных', path: '/tickets?assignee=unassigned' } : null,
  ].filter(Boolean) as Array<{ key: string; severity: number; badge: string; tone: 'danger' | 'warning'; title: string; note: string; path: string }>);
  const attentionItems = ([
    ...ticketAlerts,
    ...serviceAlerts,
    ...fallbackTicketAlerts,
    diagnosticsState === 'ready' && (!registered || !gateway) && !hasTelephonyServiceAlert ? { key: 'telephony-diagnostics', severity: 6, badge: 'Сбой', tone: 'danger' as const, title: 'Телефония', note: !registered ? 'SIP-регистрация не подтверждена' : 'Нет связи с FreeSWITCH', path: '/telephony?section=DIAGNOSTICS' } : null,
    data.operational.missed_calls > 0 ? { key: 'missed-calls', severity: 4, badge: String(data.operational.missed_calls), tone: 'danger' as const, title: 'Пропущенные звонки', note: 'Клиенты ожидают обратного звонка', path: '/telephony?section=CALLS' } : null,
    data.operational.unread_conversations > 0 ? { key: 'unread-conversations', severity: 3, badge: String(data.operational.unread_conversations), tone: 'info' as const, title: 'Непрочитанные диалоги', note: 'Контакт-центр ожидает ответа', path: '/contact-center?status=ACTIVE' } : null,
    errors > 0 ? { key: 'telephony-webhooks', severity: 5, badge: String(errors), tone: 'danger' as const, title: 'Ошибки событий телефонии', note: 'За последний час', path: '/telephony?section=DIAGNOSTICS' } : null,
    diagnosticsState === 'error' ? { key: 'telephony-unavailable', severity: 5, badge: 'Нет данных', tone: 'danger' as const, title: 'Диагностика телефонии', note: 'Проверка временно недоступна', path: '/telephony?section=DIAGNOSTICS' } : null,
  ].filter(Boolean) as Array<{ key: string; severity: number; badge: string; tone: 'danger' | 'warning' | 'info'; title: string; note: string; path: string }>)
    .sort((left, right) => right.severity - left.severity || left.title.localeCompare(right.title, 'ru'));
  const incidents = attentionItems.slice(0, 4);
  const attentionCount = attentionItems.length;
  const attentionLabel = attentionCount
    ? `${attentionCount} ${attentionCount === 1 ? 'требует' : 'требуют'} внимания`
    : 'Нет активных';
  const integrations = INTEGRATION_TILES.map(item => ({
    ...item,
    presentation: integrationPresentation(monitoring, monitoringState, item.componentKey),
  }));
  const connectedIntegrations = integrations.filter(item => item.presentation.connected).length;
  const integrationsCaption = monitoringState === 'ready'
    ? `${connectedIntegrations} из ${integrations.length} подключены`
    : monitoringState === 'error'
      ? 'Статус подключений недоступен'
      : 'Проверяем подключения';
  const cards: Partial<Record<DashboardWidgetKey, ReactNode>> = {
    system_health: <SystemHealthCard monitoring={monitoring} monitoringState={monitoringState} onNavigate={onNavigate} onRetryMonitoring={onRetryMonitoring}/>,
    server: <ServerCard serverMetrics={serverMetrics} serverMetricsState={serverMetricsState} onRetryServerMetrics={onRetryServerMetrics}/>,

    notices: <RoleCard widgetKey="notices" title="Требует внимания" caption="Только то, где сейчас нужно действие" icon={<AlertTriangle />} size={incidents.length ? 'standard' : 'compact'} className={`dashboard-role-span-4${incidents.length ? '' : ' dashboard-role-card--empty-compact'}`} action={<InlineStatus tone={attentionCount ? 'danger' : 'ok'}>{attentionLabel}</InlineStatus>}>
      {incidents.length ? <div className="dashboard-role-incidents" data-attention-list>{incidents.map(item => <a className="dashboard-role-attention-row" data-tone={item.tone} href={item.path} onClick={dashboardLink(item.path, onNavigate)} key={item.key}><i/><span><strong>{item.title}</strong><small>{item.note}</small></span><b data-attention-count={item.badge}>{item.badge}</b><ChevronRight/></a>)}</div> : <EmptyMessage title="Всё под контролем" description="Срочных действий сейчас нет"/>}
    </RoleCard>,

    summary: <RoleCard
      widgetKey="summary"
      title="Заявки"
      caption="Текущая нагрузка сервисной службы"
      icon={<TicketIcon />}
      size="compact"
      className="dashboard-role-span-12"
      action={<CardActionLink path="/tickets" label="Все заявки" onNavigate={onNavigate}/>}
    >
      <MetricTiles ariaLabel="Показатели заявок" items={[
        { key: 'active', label: 'Активные', value: data.metrics.active, note: 'Текущий объём', icon: <TicketIcon/>, tone: 'blue' },
        { key: 'new', label: 'Новые', value: data.metrics.new_count, note: 'Требуют распределения', icon: <Zap/>, tone: 'violet' },
        { key: 'unassigned', label: 'Без исполнителя', value: data.metrics.unassigned, note: 'Нужно назначить', icon: <Users/>, tone: 'amber' },
        { key: 'overdue', label: 'Просрочены', value: data.metrics.overdue, note: 'Требуют реакции', icon: <AlertTriangle/>, tone: 'red' },
        { key: 'created_month', label: 'Создано за месяц', value: data.metrics.created_month, note: worklogPeriodLabel(data.worklogs.period_month), icon: <BriefcaseBusiness/>, tone: 'slate' },
      ]}/>
    </RoleCard>,

    services: <RoleCard widgetKey="services" title="Очереди" caption="Нагрузка по направлениям" icon={<Inbox />} size="compact" className="dashboard-role-span-4 dashboard-role-card--queues-compact" action={<CardActionLink path="/tickets" label="Все очереди" onNavigate={onNavigate}/>}><QueueRows data={data.queues} limit={3}/></RoleCard>,

    telephony: <RoleCard widgetKey="telephony" title="Телефония" caption="SIP-шлюзы, браузерная телефония и вызовы" icon={<PhoneCall />} size="standard" className="dashboard-role-span-4" action={<CardActionLink path="/telephony?section=DIAGNOSTICS" label="Панель АТС" onNavigate={onNavigate}/>}>
      <div className="dashboard-role-services" data-telephony-services>
        <article data-telephony-service="beeline"><BrandMark brand="beeline"/><span><strong>SIP Транк Билайн</strong><small>{diagnosticsState === 'error' ? 'Диагностика недоступна' : registered ? 'Регистрация активна' : diagnosticsState === 'loading' ? 'Проверяем регистрацию' : 'Требует проверки'}</small></span><InlineStatus tone={diagnosticsState === 'error' ? 'danger' : registered ? 'ok' : 'warning'}>{diagnosticsState === 'error' ? 'Недоступно' : registered ? 'Подключён' : 'Проверить'}</InlineStatus></article>
        <article data-telephony-service="freeswitch"><ServiceMark service="freeswitch"/><span><strong>FreeSWITCH</strong><small>{diagnosticsState === 'error' ? 'Диагностика недоступна' : gateway ? 'Сервер отвечает' : 'Нет подтверждения связи'}</small></span><InlineStatus tone={diagnosticsState === 'error' ? 'danger' : gateway ? 'ok' : 'warning'}>{diagnosticsState === 'error' ? 'Недоступно' : gateway ? 'Работает' : 'Проверить'}</InlineStatus></article>
      </div>
      <div className="dashboard-role-telephony-summary" data-telephony-summary>
        <span><strong>{Number(diagnostics?.users?.online || 0)}</strong><small>Операторов онлайн</small><em>{Number(diagnostics?.users?.configured || 0)} настроено</em></span>
        <span><strong>{Number(diagnostics?.calls?.active || 0)}</strong><small>Активных звонков</small><em>{diagnosticsState === 'error' ? 'Нет данных' : 'Сейчас'}</em></span>
      </div>
    </RoleCard>,

    integrations: <RoleCard
      widgetKey="integrations"
      title="Интеграции"
      caption={integrationsCaption}
      icon={<Workflow />}
      size="compact"
      className="dashboard-role-span-4 dashboard-role-card--integrations-compact"
      action={<CardActionLink path="/admin" label="Управление" onNavigate={onNavigate}/>}
    >
      <div className="dashboard-role-integrations">
        {integrations.map(item => <a
          href="/admin"
          onClick={dashboardLink('/admin', onNavigate)}
          key={item.brand}
          data-integration-tile={item.brand}
        >
          <BrandMark brand={item.brand}/>
          <span>
            <strong>{item.title}</strong>
            <small className="dashboard-role-integration-status" data-tone={item.presentation.tone}>
              <i aria-hidden="true"/>{item.presentation.label}
            </small>
            <small className="dashboard-role-integration-activity">{item.presentation.activity}</small>
            <small className="dashboard-role-integration-operations">{item.presentation.operations}</small>
          </span>
          <ChevronRight/>
        </a>)}
      </div>
    </RoleCard>,

    planned: <RoleCard widgetKey="planned" title="Плановые работы" caption="Ближайшие регламентные окна" icon={<CalendarClock />} size={data.planned_works.length ? 'standard' : 'compact'} className={`dashboard-role-span-4${data.planned_works.length ? '' : ' dashboard-role-card--empty-compact'}`} action={<CardActionLink path="/planned-works" label="Все работы" onNavigate={onNavigate}/>}><PlannedRows data={data.planned_works} onNavigate={onNavigate}/></RoleCard>,
  };

  return <DashboardGrid className="dashboard-role-grid--admin" enabled layoutKey={props.enabledWidgets.join('|')}>
    {props.enabledWidgets.map(widgetKey => <Fragment key={widgetKey}>{cards[widgetKey] ?? <SupplementalWidget {...props} role="admin" widgetKey={widgetKey}/>}</Fragment>)}
  </DashboardGrid>;
}

function ManagerDashboard(props: DashboardRoleViewsProps) {
  const { data, trend, trendPeriod, trendLoading, onNavigate, onTrendPeriodChange } = props;
  const primaryRisk = data.risky[0];
  const decisions = [
    primaryRisk ? {
      title: `${primaryRisk.display_number} · ${primaryRisk.subject}`,
      note: primaryRisk.company_name || 'Заявка с риском нарушения SLA',
      path: `/tickets/${primaryRisk.id}`,
      tone: 'danger' as const,
      riskTicketId: primaryRisk.id,
    } : null,
    data.metrics.overdue ? { title: 'Нарушен SLA решения', note: `${data.metrics.overdue} просроченных заявок`, path: '/tickets?overdue=1', tone: 'danger' as const } : null,
    data.metrics.unassigned ? { title: 'Заявки остаются без исполнителя', note: `${data.metrics.unassigned} требуют распределения`, path: '/tickets?assignee=unassigned', tone: 'warning' as const } : null,
    data.operational.missed_calls ? { title: 'Просрочен обратный звонок', note: `${data.operational.missed_calls} пропущенных вызова`, path: '/telephony?section=CALLS', tone: 'danger' as const } : null,
    data.operational.unread_conversations ? { title: 'Клиенты ожидают ответа', note: `${data.operational.unread_conversations} непрочитанных диалога`, path: '/contact-center?status=ACTIVE', tone: 'info' as const } : null,
  ].filter(Boolean) as Array<{ title: string; note: string; path: string; tone: 'danger' | 'warning' | 'info'; riskTicketId?: string }>;

  const cards: Partial<Record<DashboardWidgetKey, ReactNode>> = {
    summary: <RoleCard widgetKey="summary" title="Операционные показатели" caption="Текущий рабочий объём и изменения за день" icon={<BarChart3 />} className="dashboard-role-span-12" action={<CardActionLink path="/tickets" label="Открыть аналитику" onNavigate={onNavigate}/>}>
      <MetricTiles ariaLabel="Операционные показатели" items={[
        { label: 'Активные', value: data.metrics.active, note: 'Сейчас в работе', icon: <TicketIcon/>, tone: 'blue' },
        { label: 'Новые сегодня', value: data.metrics.new_count, note: 'Требуют обработки', icon: <Zap/>, tone: 'violet' },
        { label: 'Решено сегодня', value: data.metrics.resolved_today, note: 'Завершено командой', icon: <CheckCircle2/>, tone: 'green' },
        { label: 'Без исполнителя', value: data.metrics.unassigned, note: 'Нужно распределить', icon: <Users/>, tone: 'amber' },
        { label: 'Просрочены', value: data.metrics.overdue, note: 'Требуют вмешательства', icon: <AlertTriangle/>, tone: 'red' },
      ]}/>
    </RoleCard>,

    notices: <RoleCard widgetKey="notices" title="Требует решения руководителя" caption="Исключения, которые нельзя оставлять в обычной очереди" icon={<AlertTriangle />} className="dashboard-role-span-12" action={<InlineStatus tone={decisions.length ? 'danger' : 'ok'}>{decisions.length ? `${decisions.length} действий` : 'Всё штатно'}</InlineStatus>}>
      {decisions.length ? <div className="dashboard-role-decisions">{decisions.map((item, index) => <a href={item.path} data-manager-risk-ticket={item.riskTicketId} onClick={dashboardLink(item.path, onNavigate)} key={item.title}><span className={`dashboard-role-decision-order dashboard-role-decision-order--${item.tone}`}>{index + 1}</span><span><strong>{item.title}</strong><small>{item.note}</small></span><em>Разобрать</em><ChevronRight/></a>)}</div> : <EmptyMessage title="Решений не требуется" description="Управленческих исключений нет"/>}
    </RoleCard>,
    trend: <TrendCard trend={trend} period={trendPeriod} loading={trendLoading} onPeriodChange={onTrendPeriodChange}/>,
    sla: <RoleCard widgetKey="sla" title="SLA и риски" caption="Ближайшие и нарушенные сроки" icon={<Clock3 />} className="dashboard-role-span-5" action={<InlineStatus tone={data.metrics.overdue ? 'danger' : 'ok'}>{data.metrics.overdue ? `${data.metrics.overdue} просрочено` : 'Без нарушений'}</InlineStatus>}><SlaRows tickets={data.risky.slice(0, 3)} onNavigate={onNavigate}/></RoleCard>,
    workload: <RoleCard widgetKey="workload" title="Загрузка команды" caption="Активные и просроченные задачи сотрудников" icon={<Users />} className="dashboard-role-span-6"><WorkloadRows data={data.staff_workload}/></RoleCard>,
    services: <RoleCard widgetKey="services" title="Очереди и направления" caption="Текущая нагрузка сервисных линий" icon={<Inbox />} className="dashboard-role-span-6"><QueueRows data={data.queues}/></RoleCard>,
    unread_contacts: <RoleCard widgetKey="unread_contacts" title="Нагрузка по каналам" caption="Обращения, ожидающие реакции команды" icon={<MessageCircle />} className="dashboard-role-span-6"><MetricTiles ariaLabel="Нагрузка по каналам" items={[
      { label: 'Диалоги', value: data.operational.unread_conversations, note: 'Непрочитанные', icon: <MessageCircle/>, tone: 'violet' },
    ]}/><ContextLink path="/contact-center?status=ACTIVE" label="Открыть контакт-центр" onNavigate={onNavigate}/></RoleCard>,
    missed_calls: <RoleCard widgetKey="missed_calls" title="Пропущенные звонки" caption="Вызовы и обязательства по обратной связи" icon={<PhoneMissed />} className="dashboard-role-span-6"><CallSummary value={data.operational.missed_calls} onNavigate={onNavigate}/></RoleCard>,
    worklogs: <RoleCard widgetKey="worklogs" title="Трудозатраты" icon={<Timer />} size="compact" className="dashboard-role-span-6 dashboard-role-card--worklog" action={<WorklogAction data={data} onNavigate={onNavigate}/>}><WorklogSummary data={data} showAmount/></RoleCard>,
    planned: <RoleCard widgetKey="planned" title="Плановые работы команды" caption="Ближайшие сервисные окна" icon={<CalendarClock />} className={`dashboard-role-span-6${data.planned_works.length ? '' : ' dashboard-role-card--empty-compact'}`} action={<CardActionLink path="/planned-works" label="Календарь" onNavigate={onNavigate}/>}><PlannedRows data={data.planned_works} onNavigate={onNavigate}/></RoleCard>,
  };

  return <DashboardGrid className="dashboard-role-grid--manager" enabled layoutKey={props.enabledWidgets.join('|')}>
    {props.enabledWidgets.map(widgetKey => <Fragment key={widgetKey}>{cards[widgetKey] ?? <SupplementalWidget {...props} role="manager" widgetKey={widgetKey}/>}</Fragment>)}
  </DashboardGrid>;
}

function SpecialistDashboard(props: DashboardRoleViewsProps) {
  const { data, onNavigate } = props;
  const waiting = data.awaiting ?? [];
  const focusTickets = [...data.risky, ...waiting].filter((ticket, index, items) => items.findIndex(item => item.id === ticket.id) === index);
  const focusItems: Array<{ title: string; note: string; path: string; icon: ReactNode; tone: string }> = [];
  if (focusTickets[0]) focusItems.push({ title: `Открыть ${focusTickets[0].display_number}`, note: focusTickets[0].subject, path: `/tickets/${focusTickets[0].id}`, icon: <AlertTriangle/>, tone: 'red' });
  if (data.operational.missed_calls) focusItems.push({ title: 'Обработать пропущенные звонки', note: `${data.operational.missed_calls} вызова ожидают реакции`, path: '/telephony?section=CALLS', icon: <PhoneMissed/>, tone: 'amber' });
  if (data.operational.unread_conversations) focusItems.push({ title: 'Ответить в контакт-центре', note: `${data.operational.unread_conversations} непрочитанных диалога`, path: '/contact-center?status=ACTIVE', icon: <MessageCircle/>, tone: 'violet' });
  if (data.planned_works[0]) focusItems.push({ title: data.planned_works[0].title, note: formatShort(data.planned_works[0].scheduled_start), path: `/planned-works?work=${data.planned_works[0].id}`, icon: <CalendarClock/>, tone: 'blue' });

  const cards: Partial<Record<DashboardWidgetKey, ReactNode>> = {
    awaiting: <RoleCard widgetKey="awaiting" title="Мой фокус" caption="Следующие действия собраны по срочности" icon={<Gauge />} className="dashboard-role-span-12" action={<InlineStatus tone={focusItems.length ? 'danger' : 'ok'}>{focusItems.length ? `${focusItems.length} действия` : 'Всё выполнено'}</InlineStatus>}>
      {focusItems.length ? <div className="dashboard-role-focus">{focusItems.slice(0, 4).map((item, index) => <a href={item.path} onClick={dashboardLink(item.path, onNavigate)} key={`${item.path}-${index}`}><span className={`dashboard-role-focus__order dashboard-role-tone--${item.tone}`}>{index + 1}</span><span>{item.icon}<b>{item.title}</b><small>{item.note}</small></span><em>Открыть</em><ChevronRight/></a>)}</div> : <EmptyMessage title="Срочных действий нет" description="Все обязательства обработаны"/>}
    </RoleCard>,

    summary: <RoleCard widgetKey="summary" title="Мои показатели" caption="Только ваша текущая рабочая область" icon={<Activity />} className="dashboard-role-span-12" action={<CardActionLink path="/tickets" label="Открыть мои заявки" onNavigate={onNavigate}/>}>
      <MetricTiles ariaLabel="Мои показатели" items={[
        { label: 'Новые', value: data.metrics.new_count, note: 'Требуют обработки', icon: <Zap/>, tone: 'violet' },
        { label: 'Активные', value: data.metrics.active, note: 'В вашем контуре', icon: <TicketIcon/>, tone: 'blue' },
        { label: 'В работе', value: data.metrics.in_progress, note: 'Активная обработка', icon: <Activity/>, tone: 'green' },
        { label: 'Жду клиента', value: data.metrics.waiting_client, note: 'Ответ уже запрошен', icon: <MessageCircle/>, tone: 'amber' },
        { label: 'Просрочены', value: data.metrics.overdue, note: 'Требуют реакции', icon: <AlertTriangle/>, tone: 'red' },
        { label: 'Решено сегодня', value: data.metrics.resolved_today, note: 'Личный результат', icon: <CheckCircle2/>, tone: 'green' },
      ]}/>
    </RoleCard>,
    sla: <RoleCard widgetKey="sla" title="Мои сроки SLA" caption="Ближайшие и нарушенные сроки" icon={<Clock3 />} className="dashboard-role-span-5"><SlaRows tickets={data.risky.slice(0, 3)} onNavigate={onNavigate}/></RoleCard>,
    recent: <RoleCard widgetKey="recent" title="Мои заявки" caption="Последние назначенные и изменённые обращения" icon={<TicketIcon />} className="dashboard-role-span-7" action={<CardActionLink path="/tickets" label="Все мои заявки" onNavigate={onNavigate}/>}><TicketTable tickets={data.recent.slice(0, 3)} onNavigate={onNavigate} label="specialist"/></RoleCard>,
    unread_contacts: <RoleCard widgetKey="unread_contacts" title="Непрочитанные диалоги" caption="Обращения, ожидающие вашего ответа" icon={<MessageCircle />} className="dashboard-role-span-4" action={<InlineStatus tone={data.operational.unread_conversations ? 'warning' : 'ok'}>{data.operational.unread_conversations} новых</InlineStatus>}><ChannelSummary value={data.operational.unread_conversations} onNavigate={onNavigate}/></RoleCard>,
    missed_calls: <RoleCard widgetKey="missed_calls" title="Мои обратные звонки" caption="Пропущенные вызовы, требующие реакции" icon={<PhoneMissed />} className="dashboard-role-span-4"><CallSummary value={data.operational.missed_calls} onNavigate={onNavigate}/></RoleCard>,
    workload: <RoleCard widgetKey="workload" title="Свободные заявки моих очередей" caption="Можно взять в работу прямо сейчас" icon={<BriefcaseBusiness />} className="dashboard-role-span-4" action={<InlineStatus tone={data.metrics.unassigned ? 'info' : 'ok'}>{data.metrics.unassigned} доступны</InlineStatus>}><QueueRows data={data.queues}/><ContextLink path="/tickets?assignee=unassigned" label="Открыть свободную очередь" onNavigate={onNavigate}/></RoleCard>,
    planned: <RoleCard widgetKey="planned" title="Мои плановые работы" caption="Обязательства на ближайшие дни" icon={<CalendarClock />} className={`dashboard-role-span-6${data.planned_works.length ? '' : ' dashboard-role-card--empty-compact'}`} action={<CardActionLink path="/planned-works" label="Календарь" onNavigate={onNavigate}/>}><PlannedRows data={data.planned_works} onNavigate={onNavigate}/></RoleCard>,
    worklogs: <RoleCard widgetKey="worklogs" title="Мои трудозатраты" icon={<Timer />} size="compact" className="dashboard-role-span-6 dashboard-role-card--worklog" action={<WorklogAction data={data} onNavigate={onNavigate}/>}><WorklogSummary data={data} showAmount={false}/></RoleCard>,
  };

  return <DashboardGrid className="dashboard-role-grid--specialist" enabled layoutKey={props.enabledWidgets.join('|')}>
    {props.enabledWidgets.map(widgetKey => <Fragment key={widgetKey}>{cards[widgetKey] ?? <SupplementalWidget {...props} role="specialist" widgetKey={widgetKey}/>}</Fragment>)}
  </DashboardGrid>;
}

function ClientDashboard(props: DashboardRoleViewsProps) {
  const { data, trend, trendPeriod, trendLoading, onNavigate, onTrendPeriodChange } = props;
  const cards: Partial<Record<DashboardWidgetKey, ReactNode>> = {
    summary: <RoleCard widgetKey="summary" title="Мои обращения" caption="Текущее состояние ваших заявок" icon={<TicketIcon />} className="dashboard-role-span-12"><MetricTiles ariaLabel="Показатели обращений" items={[
      { label: 'Активные', value: data.metrics.active, note: 'Открытые обращения', icon: <TicketIcon/>, tone: 'blue' },
      { label: 'В работе', value: data.metrics.in_progress, note: 'Обрабатываются', icon: <Activity/>, tone: 'green' },
      { label: 'Ожидают ответа', value: data.metrics.waiting_client, note: 'Нужна ваша реакция', icon: <MessageCircle/>, tone: 'amber' },
      { label: 'Решены сегодня', value: data.metrics.resolved_today, note: 'Завершены', icon: <CheckCircle2/>, tone: 'green' },
    ]}/></RoleCard>,
    recent: <RoleCard widgetKey="recent" title="Последние заявки" caption="Недавно обновлённые обращения" icon={<TicketIcon />} className="dashboard-role-span-7" action={<CardActionLink path="/tickets" label="Все мои заявки" onNavigate={onNavigate}/>}><TicketTable tickets={data.recent.slice(0, 3)} onNavigate={onNavigate} label="client"/></RoleCard>,
    awaiting: <RoleCard widgetKey="awaiting" title="Требуют вашего ответа" caption="Обращения, где нужна реакция" icon={<CircleDot />} className="dashboard-role-span-5"><AwaitingRows tickets={(data.awaiting ?? []).slice(0, 3)} onNavigate={onNavigate}/></RoleCard>,
    trend: <TrendCard trend={trend} period={trendPeriod} loading={trendLoading} onPeriodChange={onTrendPeriodChange}/>,
  };
  return <DashboardGrid className="dashboard-role-grid--client" enabled layoutKey={props.enabledWidgets.join('|')}>
    {props.enabledWidgets.map(widgetKey => <Fragment key={widgetKey}>{cards[widgetKey] ?? <SupplementalWidget {...props} role="client" widgetKey={widgetKey}/>}</Fragment>)}
  </DashboardGrid>;
}

export function DashboardRoleViews({ role, ...props }: DashboardRoleViewsProps & { role: DashboardRole }) {
  const [draggedKey, setDraggedKey] = useState<DashboardWidgetKey | null>(null);
  const [dropKey, setDropKey] = useState<DashboardWidgetKey | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const draggedKeyRef = useRef<DashboardWidgetKey | null>(null);
  const lastPreviewRef = useRef<DashboardWidgetKey | null>(null);
  const committedRef = useRef(false);
  const start = (key: DashboardWidgetKey, dataTransfer: DataTransfer) => {
    if (props.layoutSaving) return;
    draggedKeyRef.current = key;
    lastPreviewRef.current = key;
    committedRef.current = false;
    setDraggedKey(key);
    setDropKey(key);
    dataTransfer.effectAllowed = 'move';
    dataTransfer.setData('text/plain', key);
  };
  const preview = (targetKey: DashboardWidgetKey) => {
    const key = draggedKeyRef.current;
    if (!key || key === targetKey || lastPreviewRef.current === targetKey || props.layoutSaving) return;
    lastPreviewRef.current = targetKey;
    setDropKey(targetKey);
    props.onPreviewWidgetMove(key, targetKey);
    const targetIndex = props.enabledWidgets.indexOf(targetKey);
    setAnnouncement(`Карточка перемещена на позицию ${targetIndex + 1}`);
  };
  const drop = () => {
    if (!draggedKeyRef.current || props.layoutSaving) return;
    committedRef.current = true;
    props.onCommitWidgetMove();
    setDraggedKey(null);
    setDropKey(null);
  };
  const end = () => {
    if (draggedKeyRef.current && !committedRef.current) props.onCancelWidgetMove();
    draggedKeyRef.current = null;
    lastPreviewRef.current = null;
    committedRef.current = false;
    setDraggedKey(null);
    setDropKey(null);
  };
  const content = role === 'admin'
    ? <AdminDashboard {...props}/>
    : role === 'manager'
      ? <ManagerDashboard {...props}/>
      : role === 'client'
        ? <ClientDashboard {...props}/>
        : <SpecialistDashboard {...props}/>;
  return <LayoutEditorContext.Provider value={{
    saving: props.layoutSaving,
    orderedKeys: props.enabledWidgets,
    draggedKey,
    dropKey,
    start,
    preview,
    drop,
    end,
  }}>
    {content}
    <span className="sr-only" aria-live="polite">{announcement}</span>
  </LayoutEditorContext.Provider>;
}
